import { faBalanceScaleLeft, faSearch } from "@fortawesome/pro-solid-svg-icons";
import { parse, stringify } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { getFormValues } from "redux-form";

import { analyticsStoreEvent } from "@/mk2/actions";
import {
    strollersComparisonLoad,
    strollersComparisonToggle,
} from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.actions";
import strollersComparisonReducer from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.reducers";
import strollersComparisonSaga from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.sagas";
import { getStrollersComparisonState } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.selectors";
import { extractFacetItems } from "@/mk2/apps/strollers/containers/StrollersSearch/FacetItem";
import {
    StrollerSearchFormData,
    deserializeFromUrl,
    serializeToQS,
} from "@/mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm";
import { StrollersSearch } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch";
import {
    strollersSearchResultsFetchTrigger,
    strollersSearchSubmitForm,
    strollersSearchToggleMoreFacets,
} from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.actions";
import {
    ChosenFacetValue,
    PageMode,
    STROLLERS_SEARCH_FORM_NAME,
    getFacetSetup,
    getQueryString,
} from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import reducer, {
    initialStrollersResultState,
} from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.reducers";
import saga from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.sagas";
import { getStrollersSearchState } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.selectors";
import { convertStrollerType } from "@/mk2/apps/strollers/helpers";
import {
    strollersComparisonUrl,
    strollersSearchUrl,
    strollersUrl,
} from "@/mk2/apps/strollers/urls";
import { Btn, BtnCountType, BtnLayout, BtnType } from "@/mk2/components/Btn";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import {
    mkReduxFormMapDispatchToProps,
    mkReduxFormMapStateToProps,
} from "@/mk2/decorators/mkReduxForm";
import { cachedInitialValues } from "@/mk2/helpers/form";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import FormPage, { FormPageDispatchProps, FormPageStateProps } from "@/mk2/pages/FormPage";
import { HalfpageProps, PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestPermissions } from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import {
    ContentItemEntity,
    ContentItemSchema,
    PhotoDetailSchema,
    StrollerBrandEntity,
    StrollerBrandSchema,
    StrollerEntity,
    StrollerPhoto,
    StrollerSchema,
    StrollerVariantEntity,
    StrollerVariantSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_TITLE_STROLLER_SEARCH,
    STROLLERS_COMPARISON,
    STROLLERS_SEARCH_RESULTS_results,
    STROLLER_SEARCH_SUBMIT,
    STROLLER_SEARCH_TITLE,
} from "@/mk/autogenerated/translation";

interface OwnProps {}

interface StateProps extends FormPageStateProps {
    /* form state props */
    initialValues: StrollerSearchFormData;
    currentValues: StrollerSearchFormData;
    searchFacetsHits: number;
    showAdditionalFacets: boolean;

    /* search result state props */
    activeFacets: ChosenFacetValue[];
    hasMore: boolean;
    count: number;
    items: StrollerEntity[];
    strollersPreviewPhotoMap: Record<number, number>;
    variants: StrollerVariantEntity[];
    variantsPreviewPhotoMap: Record<number, number>;
    strollersPhotosMap: Record<number, StrollerPhoto>;
    strollerIdToVariantsMap: Record<number, StrollerVariantEntity[]>;
    brandsOfQuery: StrollerBrandEntity[];
    searchInfos: ContentItemEntity[];
    buyingGuide: ContentItemEntity;
    loadingState: LoadingState;
    errorMessage: string;
    comparison: number[];

    brands: StrollerBrandEntity[];
    brandsCount: number;
    strollersCountPerBrand: Record<number, number>;
    reviewsCountPerBrand: Record<number, number>;

    isLandingPageForStrollerType: string;
    queryString: string;
    formData: any;
    stringFacetItems: string[];
    renderedFacetItems: React.ReactNode[];

    requestUserPermissions: string[];
}

interface DispatchProps extends FormPageDispatchProps {
    onToggleMoreFacets();
    onSave(values: StrollerSearchFormData);
    onStoreEvent(name: string, props: any);

    onLoad(q: string, offset?: number);

    onStrollersComparisonLoad();
    onStrollersComparisonToggle(strollerId, location: HistoryLocationWithState);
}

type RouteProps = PageRouteProps<{
    pageMode: PageMode;
    strollerType?: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

const formSelector = getFormValues(STROLLERS_SEARCH_FORM_NAME);

export default class StrollersSearchPage extends FormPage<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                location,
                routeParams: { pageMode, strollerType },
            } = ownProps;

            const strollersSearchState = getStrollersSearchState(state);
            const strollersComparisonState = getStrollersComparisonState(state);
            const requestUserPermissions = getRequestPermissions(state);

            const currentValues = formSelector(state) as StrollerSearchFormData;
            const searchFacetsHits = currentValues
                ? getStrollersSearchState(state).searchFacetsCounts[
                      stringify(serializeToQS(currentValues))
                  ]?.hits
                : null;
            const queryString = getQueryString(pageMode, location.search, strollerType);
            const formData = deserializeFromUrl(queryString, requestUserPermissions);

            let activeFacets = null;

            const parsedSearch = parse(queryString);
            if (pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_TYPE) {
                activeFacets = [];
                const chosenFacetValue: ChosenFacetValue = {
                    facetSetup: getFacetSetup("type"),
                    chosenRawValues: [convertStrollerType(strollerType, "-to_")],
                };
                activeFacets.push(chosenFacetValue);
            }
            if (pageMode === PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY) {
                activeFacets = [];
                for (const key of Object.keys(parsedSearch)) {
                    const facetSetup = getFacetSetup(key);
                    if (!facetSetup) {
                        // Ignore unknown key
                        continue;
                    }
                    const chosenFacetValue: ChosenFacetValue = {
                        facetSetup,
                        chosenRawValues: !Array.isArray(parsedSearch[key])
                            ? [parsedSearch[key] as string]
                            : (parsedSearch[key] as string[]),
                    };
                    activeFacets.push(chosenFacetValue);
                }
            }

            const searchResultState =
                strollersSearchState.results[queryString] || initialStrollersResultState;
            const strollersPreviewPhotoMap = searchResultState.strollersPreviewPhotoMap;
            const variantsPreviewPhotoMap = searchResultState.variantsPreviewPhotoMap;

            const variants = getDenormalizedEntities<StrollerVariantEntity>(
                state,
                StrollerVariantSchema,
                searchResultState.variantIds,
            );
            const strollerIdToVariantsMap = {};
            variants.map((variant) => {
                if (!strollerIdToVariantsMap[variant.strollerId]) {
                    strollerIdToVariantsMap[variant.strollerId] = [variant];
                } else {
                    strollerIdToVariantsMap[variant.strollerId].push(variant);
                }
            });

            const strollersPhotos = getDenormalizedEntities<StrollerPhoto>(
                state,
                PhotoDetailSchema,
                searchResultState.strollersPhotosIds,
            );
            const strollersPhotosMap = {};
            strollersPhotos.map(
                (strollerPhoto) => (strollersPhotosMap[strollerPhoto.id] = strollerPhoto),
            );

            const facetItemsResults = extractFacetItems(pageMode, formData, location);
            const { isLandingPageForStrollerType, stringFacetItems, renderedFacetItems } =
                facetItemsResults;

            return {
                ...mkReduxFormMapStateToProps(STROLLERS_SEARCH_FORM_NAME)(state),

                /* form state props */
                initialValues: cachedInitialValues(STROLLERS_SEARCH_FORM_NAME, formData, [
                    pageMode,
                    `${location.pathname}${queryString}${location.hash}`,
                ]),
                currentValues: getFormValues(STROLLERS_SEARCH_FORM_NAME)(
                    state,
                ) as StrollerSearchFormData,
                searchFacetsHits,
                showAdditionalFacets: strollersSearchState.showAdditionalFacets,

                comparison: strollersComparisonState.allIds,

                /* search result state props */
                activeFacets,
                hasMore: searchResultState.hasMore,
                count: searchResultState.count,
                items: getDenormalizedEntities<StrollerEntity>(
                    state,
                    StrollerSchema,
                    searchResultState.strollerIds,
                ),
                strollersPreviewPhotoMap,
                variants,
                variantsPreviewPhotoMap,
                strollersPhotosMap,
                strollerIdToVariantsMap,
                brandsOfQuery: getDenormalizedEntities<StrollerBrandEntity>(
                    state,
                    StrollerBrandSchema,
                    searchResultState.brandsOfQueryIds,
                ),
                searchInfos: getDenormalizedEntities<ContentItemEntity>(
                    state,
                    ContentItemSchema,
                    searchResultState.searchInfoIds,
                ),
                buyingGuide: getDenormalizedEntity<ContentItemEntity>(
                    state,
                    ContentItemSchema,
                    strollersSearchState.buyingGuideId,
                ),
                loadingState: searchResultState.loadingState,
                errorMessage: searchResultState.errorMessage,

                brands: getDenormalizedEntities<StrollerBrandEntity>(
                    state,
                    StrollerBrandSchema,
                    strollersSearchState.brandIds,
                ),
                brandsCount: strollersSearchState.brandsCount,
                strollersCountPerBrand: strollersSearchState.strollersCountPerBrand,
                reviewsCountPerBrand: strollersSearchState.reviewsCountPerBrand,
                isLandingPageForStrollerType,
                queryString,
                formData,
                stringFacetItems,
                renderedFacetItems,
                requestUserPermissions,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            ...mkReduxFormMapDispatchToProps(STROLLERS_SEARCH_FORM_NAME),
            onToggleMoreFacets: strollersSearchToggleMoreFacets,
            onSave: strollersSearchSubmitForm,
            onStoreEvent: analyticsStoreEvent,
            onLoad: strollersSearchResultsFetchTrigger,
            onStrollersComparisonLoad: strollersComparisonLoad,
            onStrollersComparisonToggle: strollersComparisonToggle,
        };
    }

    public getMetaTitle(props: Props): string {
        const { isLandingPageForStrollerType, stringFacetItems } = props;
        return isLandingPageForStrollerType
            ? isLandingPageForStrollerType
            : stringFacetItems?.length
              ? interpolate(STROLLERS_SEARCH_RESULTS_results, {
                    results: stringFacetItems.join(", "),
                })
              : SEO_TITLE_STROLLER_SEARCH;
    }

    public render(props: Props): false | JSX.Element {
        return <StrollersSearch {...props} />;
    }

    public getSubmitButton(props: Props): React.ReactElement {
        const {
            routeParams: { pageMode },
            searchFacetsHits,
        } = props;
        if (pageMode === PageMode.SHOW_SEARCH_FORM) {
            return (
                <SubmitButton
                    label={`${STROLLER_SEARCH_SUBMIT} (${searchFacetsHits ?? "..."})`}
                    formName={STROLLERS_SEARCH_FORM_NAME}
                />
            );
        }
        return this.getHeaderRightWhenNoForm(props);
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHeaderLeft(props: Props): React.ReactElement {
        const {
            routeParams: { pageMode },
        } = props;
        if (pageMode === PageMode.SHOW_SEARCH_FORM) {
            return (
                <Btn
                    icon="mkicon-arrow-back@3x.png"
                    layout={BtnLayout.Icon}
                    link={url(strollersUrl, {})}
                />
            );
        } else {
            const backUrl = this.getDefaultBackPath(props);
            const { queryString, requestUserPermissions, searchFacetsHits, onStoreEvent } = props;
            const parsedQuery = parse(queryString);
            return (
                <Btn
                    icon="mkicon-arrow-back@3x.png"
                    layout={BtnLayout.Icon}
                    link={url(backUrl, { pageMode: PageMode.SHOW_SEARCH_FORM }, parsedQuery)}
                    onClick={this.handleOnCancel(
                        onStoreEvent,
                        requestUserPermissions,
                        searchFacetsHits,
                        parsedQuery,
                    )}
                />
            );
        }
    }

    public getDefaultBackPath(props: Props): string {
        return strollersSearchUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { items, loadingState } = props;
        if (isSuccess(loadingState) && items?.length > 0) {
            return {
                zone: "Strollers Index - Halfpage Sticky (Desktop)",
            };
        }
    }

    public getHeaderTitle(props: Props): string {
        const {
            routeParams: { pageMode },
        } = props;
        if (pageMode === PageMode.SHOW_SEARCH_FORM) {
            return STROLLER_SEARCH_TITLE;
        } else {
            return null;
        }
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        return null;
    }

    public getHeaderRightWhenNoForm(props: Props): React.ReactElement {
        const { queryString, requestUserPermissions, searchFacetsHits, onStoreEvent } = props;
        const parsedQuery = parse(queryString);

        return (
            <>
                <Btn
                    type={BtnType.BlueText}
                    layout={BtnLayout.Icon}
                    icon={faBalanceScaleLeft}
                    count={props.comparison.length}
                    countType={BtnCountType.Circle}
                    link={strollersComparisonUrl}
                />
                <Btn
                    type={BtnType.BlueText}
                    layout={BtnLayout.Icon}
                    icon={faSearch}
                    link={url(
                        strollersSearchUrl,
                        { pageMode: PageMode.SHOW_SEARCH_FORM },
                        parsedQuery,
                    )}
                    onClick={this.handleOnCancel(
                        onStoreEvent,
                        requestUserPermissions,
                        searchFacetsHits,
                        parsedQuery,
                    )}
                />
            </>
        );
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.strollers.strollersSearch", reducer);
        store.injectSaga("containers.strollers.strollersSearch", saga);

        store.injectReducer("containers.strollers.strollersComparison", strollersComparisonReducer);
        store.injectSaga("containers.strollers.strollersComparison", strollersComparisonSaga);
    }

    private handleOnCancel =
        (onStoreEvent, requestUserPermissions, searchFacetsHits, parsedQuery) => () => {
            onStoreEvent("strollers_search", {
                query_phrase: parsedQuery.strollers_query || null,
                query_obj: JSON.stringify(parsedQuery),
                is_admin: requestUserPermissions.indexOf("auth.can_manage_strollers") >= 0,
                num_results: searchFacetsHits,
                action: "cancel",
            });
        };
}
